import {
  Alert,
  Badge,
  Button,
  breakpoints,
  Col,
  CopyButton,
  Icon,
  Input,
  ListRow,
  Select,
  Spinner,
  Stack,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { failureSaid } from "../localization/failure.ts";
import { howLongAgo } from "../localization/format.ts";
import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";

export type Role = "owner" | "admin" | "member";

type State = "live" | "spent" | "revoked" | "expired";

type Listed = {
  state: State;
  token: string | null;
  role: Role;
  invitedBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  redeemedBy: string | null;
  redeemedAt: string | null;
};

export type Place = { kind: "server" } | { kind: "project"; id: string; name: string };

const saidState = (): Record<State, string> => ({
  live: say("invite.state.live"),
  spent: say("invite.state.spent"),
  revoked: say("invite.state.revoked"),
  expired: say("invite.state.expired"),
});

const saidRole = (): Record<Role, string> => ({
  member: say("invite.role.member"),
  admin: say("invite.role.admin"),
  owner: say("invite.role.owner"),
});

export function linkTo(token: string): string {
  return `${window.location.origin}/register/${token}`;
}

export function InviteBox({ place }: { place: Place }) {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  const [invites, setInvites] = useState<Listed[] | null>(null);
  const [role, setRole] = useState<Role>("member");
  const [mayGiveOwner, setMayGiveOwner] = useState(false);
  const [minted, setMinted] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const scope = place.kind === "project" ? place.id : "";

  const again = useCallback(() => {
    if (scope === "") {
      Promise.all([read<Listed[]>("server.invites"), read<{ role: string | null }>("me")])
        .then(([listed, me]) => {
          setInvites(listed);
          setMayGiveOwner(me.role === "owner");
        })
        .catch((cause: unknown) => setRefused(sentence(cause)));
      return;
    }
    read<{ invites: Listed[]; iAm: Role | null }>("project.invites", { project: scope })
      .then((said) => {
        setInvites(said.invites);
        setMayGiveOwner(said.iAm === "owner");
      })
      .catch((cause: unknown) => setRefused(sentence(cause)));
  }, [scope]);
  useEffect(again, [again]);

  const make = async (): Promise<void> => {
    setWorking(true);
    setRefused(null);
    try {
      const made =
        place.kind === "server"
          ? await act<{ token: string }>("admin.invite", { role })
          : await act<{ token: string }>("project.invite", { project: place.id, role });
      setMinted(made.token);
      again();
    } catch (cause) {
      setRefused(sentence(cause));
    } finally {
      setWorking(false);
    }
  };

  const takeBack = async (token: string): Promise<void> => {
    setWorking(true);
    setRefused(null);
    try {
      if (place.kind === "server") await act("admin.revokeInvite", { token });
      else await act("project.revokeInvite", { project: place.id, token });
      if (minted === token) setMinted(null);
      again();
    } catch (cause) {
      setRefused(sentence(cause));
    } finally {
      setWorking(false);
    }
  };

  const choices = [
    { label: saidRole().member, value: "member" },
    { label: saidRole().admin, value: "admin" },
    ...(mayGiveOwner ? [{ label: saidRole().owner, value: "owner" }] : []),
  ];

  const arrived = (invites ?? []).filter((one) => one.state === "spent" && one.role === "owner");

  return (
    <Stack gap={3} testID="invites">
      <Col
        style={{
          flexDirection: narrow ? "column" : "row",
          alignItems: narrow ? "stretch" : "center",
          gap: theme.space[2],
        }}
      >
        <Col style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <Text typeName="bodySemibold" color="text.primary">
            {say("invite.title")}
          </Text>
          <Text typeName="caption" color="text.secondary">
            {place.kind === "server" ? say("invite.toServer") : say("invite.toProject")(place.name)}
          </Text>
        </Col>
        <Col style={narrow ? undefined : { minWidth: 140 }}>
          <Select
            value={role}
            onChange={(said) => setRole(said as Role)}
            options={choices}
            accessibilityLabel={say("invite.as")}
          />
        </Col>
        <Button onPress={() => void make()} disabled={working} testID="make-invite">
          {working ? say("invite.making") : say("invite.make")}
        </Button>
      </Col>

      {role === "owner" ? (
        <Text typeName="caption" color="text.tertiary">
          {say("invite.ownerWarning")}
        </Text>
      ) : null}

      {arrived.map((one) => (
        <Alert key={`${one.createdAt}-${one.redeemedBy}`} tone="warning" icon="shield">
          {say("invite.ownerArrived")(one.redeemedBy ?? "")}
        </Alert>
      ))}

      {refused ? <Alert tone="danger">{refused}</Alert> : null}

      {minted ? (
        <Col style={{ gap: theme.space[2] }} testID="invite-fresh">
          <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[1] }}>
            <Icon name="check" size={14} color="accent" />
            <Text typeName="caption" color="accent">
              {say("invite.fresh")}
            </Text>
          </Col>
          <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
            <Col style={{ flex: 1, minWidth: 0 }}>
              <Input
                value={linkTo(minted)}
                editable={false}
                settled
                accessibilityLabel={say("invite.link")}
                testID="invite-link"
              />
            </Col>
            <CopyButton text={linkTo(minted)} label={say("invite.copy")} size={40} />
          </Col>
        </Col>
      ) : null}

      {invites === null ? (
        <Spinner label={say("invite.reading")} />
      ) : invites.length === 0 ? (
        <Text typeName="caption" color="text.tertiary">
          {say("invite.none")}
        </Text>
      ) : (
        <Col>
          {invites.map((one, at) => (
            <Row
              key={one.token ?? `${one.createdAt}-${one.redeemedBy}`}
              invite={one}
              last={at === invites.length - 1}
              working={working}
              onRevoke={() => void takeBack(one.token ?? "")}
            />
          ))}
        </Col>
      )}

      <Text typeName="caption" color="text.tertiary">
        {say("invite.said")}
      </Text>
    </Stack>
  );
}

function Row({
  invite,
  last,
  working,
  onRevoke,
}: {
  invite: Listed;
  last: boolean;
  working: boolean;
  onRevoke: () => void;
}) {
  const theme = useTheme();
  const note = [
    invite.invitedBy ? say("invite.by")(invite.invitedBy) : say("invite.byMachine"),
    say("invite.goodFor")(howLongAgo(invite.createdAt)),
    invite.state === "spent" && invite.redeemedBy ? say("invite.opened")(invite.redeemedBy) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <ListRow
      testID={`invite-${invite.state}`}
      title={saidRole()[invite.role]}
      value={note}
      last={last}
      tag={
        <Badge tone={invite.role === "owner" ? "warning" : invite.state === "live" ? "success" : "neutral"}>
          {saidState()[invite.state]}
        </Badge>
      }
      {...(invite.token
        ? {
            revealAction: true,
            action: (
              <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[1] }}>
                <CopyButton text={linkTo(invite.token)} label={say("invite.copy")} size={40} />
                <Button variant="ghost" size="sm" onPress={onRevoke} disabled={working}>
                  {say("invite.revoke")}
                </Button>
              </Col>
            ),
          }
        : {})}
    />
  );
}

function sentence(cause: unknown): string {
  return failureSaid(cause, say("why.notFinished"));
}
